import React, { FC, PropsWithChildren, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import SendbirdProvider from '@sendbird/uikit-react/SendbirdProvider';
import { BaseLayout } from 'shared/components/BaseLayout';
import { getTokenFromLocalStorage } from 'shared/utils/local-storage';
import { Urls } from 'shared/constants/urls';
import { Layouts } from 'shared/constants/layouts';
import { ROUTES, TLayout } from './pages';
import { useSelector } from 'react-redux';
import { getAffiliateSelector } from 'store/settings/settings.redux';
import '@sendbird/uikit-react/dist/index.css';

const Layout: FC<PropsWithChildren<{ layout?: TLayout }>> = ({
  layout,
  children
}) => {
  if (layout) {
    const { type, ...other } = layout;

    switch (type) {
      case Layouts.Base: {
        return <BaseLayout {...other}>{children}</BaseLayout>;
      }
      default: {
        return <>{children}</>;
      }
    }
  }
  return <>{children}</>;
};

const RequireAuth: FC<PropsWithChildren<{ privateRoute: boolean }>> = ({
  privateRoute,
  children
}) => {
  const token = getTokenFromLocalStorage();

  if (privateRoute && !token) {
    return <Navigate to={Urls.LoginPage} />;
  }

  return <>{children}</>;
};

const SendbirdWrapper: React.FC<{
  children: React.ReactElement;
}> = ({ children }) => {
  const affiliate = useSelector(getAffiliateSelector);
  if (affiliate) {
    return (
      <SendbirdProvider
        appId={process.env.REACT_APP_SENDBIRD_APP_ID || ''}
        userId={affiliate.settings?.participantIdentifier}
        accessToken={affiliate.settings?.participantSecondaryIdentifier}
      >
        {children}
      </SendbirdProvider>
    );
  }
  return children;
};

export const App = () => {
  return (
    <SendbirdWrapper>
      <Routes>
        {ROUTES.map((el) => (
          <Route
            key={el.id}
            path={el.path}
            element={
              <Suspense fallback={null}>
                <RequireAuth privateRoute={el.protected}>
                  <Layout layout={el.layout}>
                    <el.component />
                  </Layout>
                </RequireAuth>
              </Suspense>
            }
          />
        ))}
      </Routes>
    </SendbirdWrapper>
  );
};
